import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import axios from 'axios';

function Changelog() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [content, setContent] = useState('');

  useEffect(() => {
    loadChangelog();
  }, []);

  const loadChangelog = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1';
      const response = await axios.get(`${API_BASE_URL}/version/changelog`);
      setContent(response.data.content);
      setError(null);
    } catch (err) {
      setError('Failed to load changelog');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const parseMarkdown = (markdown) => {
    // Simple markdown parser for changelog
    const lines = markdown.split('\n');
    const elements = [];
    let inCodeBlock = false;
    let currentList = [];

    lines.forEach((line, index) => {
      // Code blocks
      if (line.startsWith('```')) {
        inCodeBlock = !inCodeBlock;
        return;
      }

      if (inCodeBlock) {
        return;
      }

      // Headers
      if (line.startsWith('# ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${index}`}>{currentList}</ul>);
          currentList = [];
        }
        elements.push(
          <Typography key={index} variant="h3" gutterBottom sx={{ mt: 4, mb: 2 }}>
            {line.replace('# ', '')}
          </Typography>
        );
      } else if (line.startsWith('## ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${index}`}>{currentList}</ul>);
          currentList = [];
        }
        elements.push(
          <Typography key={index} variant="h4" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
            {line.replace('## ', '')}
          </Typography>
        );
      } else if (line.startsWith('### ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${index}`}>{currentList}</ul>);
          currentList = [];
        }
        elements.push(
          <Typography key={index} variant="h6" gutterBottom sx={{ mt: 2, fontWeight: 'bold', color: 'text.secondary' }}>
            {line.replace('### ', '')}
          </Typography>
        );
      } else if (line.startsWith('- ')) {
        // List items
        currentList.push(
          <li key={index} style={{ marginBottom: '8px' }}>
            <Typography variant="body1" component="span">
              {line.replace('- ', '')}
            </Typography>
          </li>
        );
      } else if (line.trim() === '') {
        if (currentList.length > 0) {
          elements.push(
            <Box component="ul" key={`list-${index}`} sx={{ pl: 2, mb: 2 }}>
              {currentList}
            </Box>
          );
          currentList = [];
        }
      } else if (line.trim()) {
        if (currentList.length > 0) {
          elements.push(
            <Box component="ul" key={`list-${index}`} sx={{ pl: 2, mb: 2 }}>
              {currentList}
            </Box>
          );
          currentList = [];
        }
        elements.push(
          <Typography key={index} variant="body1" paragraph>
            {line}
          </Typography>
        );
      }
    });

    // Add remaining list items
    if (currentList.length > 0) {
      elements.push(
        <Box component="ul" key="list-final" sx={{ pl: 2, mb: 2 }}>
          {currentList}
        </Box>
      );
    }

    return elements;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Changelog
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ maxWidth: 900 }}>
          {parseMarkdown(content)}
        </Box>
      </Paper>
    </Box>
  );
}

export default Changelog;
